import { useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { CalendarDays, Layers, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/page-header';
import { GlassSurface } from '@/components/glow-card';
import { DetailError } from '@/components/detail-page';
import { Spinner } from '@/components/spinner';
import { StatusPill, humanStatus } from '@/components/status-pill';
import { fadeUp, stagger } from '@/components/motion';
import { CalendarItemCard } from '@/features/calendars/components/calendar-item-card';
import { useCalendar } from '@/features/calendars/use-calendar';
import { useCalendarItems } from '@/features/calendars/use-calendar-items';
import { useGenerateBriefForItem } from '@/features/calendars/use-generate-brief-for-item';
import { summarizeApiError } from '@/lib/api';

export function CalendarDetailRoute(): JSX.Element {
  const { calendarId } = useParams({ strict: false }) as { calendarId: string };
  const cal = useCalendar(calendarId);
  const items = useCalendarItems(calendarId);
  const navigate = useNavigate();
  const generateBrief = useGenerateBriefForItem(calendarId);

  const handleGenerate = async (itemId: string): Promise<void> => {
    try {
      const res = await generateBrief.mutateAsync(itemId);
      toast.success(
        res.linkage === 'linked' ? 'Linked existing brief' : 'Brief queued',
      );
      await navigate({
        to: '/briefs/$briefId',
        params: { briefId: res.brief_id },
      });
    } catch (e) {
      const s = summarizeApiError(e, { fallbackTitle: "Couldn't generate brief" });
      toast.error(s.title, { description: s.description });
    }
  };

  const itemsByMonth = useMemo(() => {
    const rows = items.data?.items ?? [];
    const groups = new Map<string, typeof rows>();
    for (const it of rows) {
      const key = it.scheduled_for.slice(0, 7); // YYYY-MM
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(it);
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items.data]);

  if (cal.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={cal.error}
          entityKind="content plan"
          backTo="/calendars"
          backLabel="Calendars"
        />
      </div>
    );
  }
  if (cal.isLoading || !cal.data) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const c = cal.data;

  return (
    <motion.div className="space-y-6" initial="hidden" animate="show" variants={stagger}>
      <PageHeader
        title={`Content plan — ${new Date(c.created_at).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`}
        eyebrow="Content calendar"
        description={`${c.period_months}-month plan · ${c.capacity_per_month} pieces per month · ${humanStatus(c.status)}`}
        actions={<StatusPill status={c.status} />}
      />

      {c.rationale ? (
        <motion.section variants={fadeUp}>
          <GlassSurface className="p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
              <Sparkles className="h-4 w-4" /> Why this plan
            </div>
            <p className="whitespace-pre-line text-sm text-muted-foreground">{c.rationale}</p>
          </GlassSurface>
        </motion.section>
      ) : null}

      {c.failure_reason ? (
        <motion.section variants={fadeUp}>
          <GlassSurface className="border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-200">
            {c.failure_reason}
          </GlassSurface>
        </motion.section>
      ) : null}

      <motion.section variants={fadeUp} className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            <h2 className="text-lg font-semibold">What to publish and when</h2>
          </div>
          {items.isFetching ? <Spinner /> : null}
        </div>

        {itemsByMonth.length === 0 ? (
          <GlassSurface className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Layers className="h-6 w-6 opacity-60" />
            {c.status === 'completed'
              ? "Your plan finished but there aren't any items to schedule yet — run a fresh audit or keyword search first."
              : "We're still planning — items will appear here shortly."}
          </GlassSurface>
        ) : (
          <div className="space-y-4">
            {itemsByMonth.map(([month, rows]) => (
              <GlassSurface key={month} className="p-4">
                <div className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">
                  {new Date(`${month}-15`).toLocaleString(undefined, {
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
                <ul className="space-y-2">
                  {rows.map((it) => {
                    const isPending =
                      generateBrief.isPending && generateBrief.variables === it.id;
                    return (
                      <CalendarItemCard
                        key={it.id}
                        item={it}
                        onGenerate={(id) => void handleGenerate(id)}
                        isGenerating={isPending}
                        isAnyGenerating={generateBrief.isPending}
                      />
                    );
                  })}
                </ul>
              </GlassSurface>
            ))}
          </div>
        )}
      </motion.section>
    </motion.div>
  );
}
